import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploadInput from "@/components/ImageUploadInput";

export default function AdminGallery() {
    const [tab, setTab] = useState("banners");
    const [banners, setBanners] = useState([]);
    const [gallery, setGallery] = useState([]);
    const [creating, setCreating] = useState(null);

    function load() {
        api.get("/admin/banners").then((r) => setBanners(r.data)).catch(() => {});
        api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    }
    useEffect(load, []);

    async function saveBanner(payload) {
        try {
            await api.post("/banners", payload);
            toast.success("Banner added");
            setCreating(null);
            load();
        } catch { toast.error("Failed"); }
    }
    async function saveGallery(payload) {
        try {
            await api.post("/gallery", payload);
            toast.success("Item added");
            setCreating(null);
            load();
        } catch { toast.error("Failed"); }
    }
    async function deleteBanner(id) {
        if (!confirm("Delete?")) return;
        await api.delete(`/banners/${id}`);
        load();
    }
    async function deleteGallery(id) {
        if (!confirm("Delete?")) return;
        await api.delete(`/gallery/${id}`);
        load();
    }

    return (
        <div className="p-8" data-testid="admin-gallery">
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-display text-3xl font-bold">Visual content</h1>
                <button
                    onClick={() => setCreating(tab === "banners" ? { _type: "banner", title: "", subtitle: "", image_url: "", cta_text: "Learn more", cta_link: "/products", order: 0, is_active: true } : { _type: "gallery", title: "", description: "", media_url: "", media_type: "image", category: "Products", is_active: true })}
                    className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 font-semibold"
                >
                    <Plus className="w-4 h-4" /> Add {tab.slice(0, -1)}
                </button>
            </div>
            <div className="flex gap-1 border-b border-border mb-6">
                <button onClick={() => setTab("banners")} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "banners" ? "border-primary text-primary" : "border-transparent"}`}>Hero banners ({banners.length})</button>
                <button onClick={() => setTab("gallery")} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "gallery" ? "border-primary text-primary" : "border-transparent"}`}>Gallery ({gallery.length})</button>
            </div>
            {tab === "banners" ? (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {banners.map((b) => (
                        <div key={b.id} className="bg-white border border-border">
                            <div className="aspect-[16/9] bg-surface overflow-hidden"><img src={b.image_url} alt={b.title} className="w-full h-full object-cover" /></div>
                            <div className="p-4">
                                <div className="font-display font-bold">{b.title}</div>
                                <div className="text-xs text-muted-foreground">{b.subtitle}</div>
                                <button onClick={() => deleteBanner(b.id)} className="mt-2 text-xs text-primary inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {gallery.map((g) => (
                        <div key={g.id} className="bg-white border border-border">
                            <div className="aspect-square bg-surface overflow-hidden"><img src={g.media_url} alt={g.title} className="w-full h-full object-cover" /></div>
                            <div className="p-3">
                                <div className="text-sm font-semibold">{g.title}</div>
                                <div className="text-xs text-muted-foreground">{g.category}</div>
                                <button onClick={() => deleteGallery(g.id)} className="mt-2 text-xs text-primary inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {creating && (
                <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4" onClick={() => setCreating(null)}>
                    <form onSubmit={(e) => { e.preventDefault(); const { _type, ...payload } = creating; (_type === "banner" ? saveBanner : saveGallery)(payload); }} className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-xl font-bold">Add {creating._type}</h3>
                            <button type="button" onClick={() => setCreating(null)}><X /></button>
                        </div>
                        <div className="grid gap-3">
                            <input required placeholder="Title" value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} className="border border-border px-3 py-2" />
                            <ImageUploadInput
                                value={creating._type === "banner" ? creating.image_url : creating.media_url}
                                onChange={(v) => setCreating({ ...creating, [creating._type === "banner" ? "image_url" : "media_url"]: v })}
                                purpose={creating._type === "banner" ? "banner" : "gallery"}
                                label={creating._type === "banner" ? "Banner image" : "Gallery image"}
                            />
                            {creating._type === "banner" ? (
                                <>
                                    <input placeholder="Subtitle" value={creating.subtitle} onChange={(e) => setCreating({ ...creating, subtitle: e.target.value })} className="border border-border px-3 py-2" />
                                    <input placeholder="CTA Text" value={creating.cta_text} onChange={(e) => setCreating({ ...creating, cta_text: e.target.value })} className="border border-border px-3 py-2" />
                                    <input placeholder="CTA Link" value={creating.cta_link} onChange={(e) => setCreating({ ...creating, cta_link: e.target.value })} className="border border-border px-3 py-2" />
                                </>
                            ) : (
                                <select value={creating.category} onChange={(e) => setCreating({ ...creating, category: e.target.value })} className="border border-border px-3 py-2">
                                    {["Products", "Events", "Projects"].map((c) => <option key={c}>{c}</option>)}
                                </select>
                            )}
                        </div>
                        <button type="submit" className="mt-4 w-full bg-primary text-white py-2.5 font-semibold">Save</button>
                    </form>
                </div>
            )}
        </div>
    );
}
